/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : UserManagement.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useEffect } from "react";
import ProjectAccess from "./ProjectAccess";
import {
  Box,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import RolesAndAccessMain from "./RolesAndAccessMain";
import Users from "./Users";

import { useDispatch } from "react-redux";
import {
  HandleApiActions,
  profileActions,
  settingsApis,
  userManagementApis,
} from "../../redux/actions";
import { useLocation } from "react-router-dom";
import Policies from "./Policies";
import RoleType from "./RoleType";

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 1 }}>{children}</Box>}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function UserManagement() {
  const dispatch = useDispatch();

  const location = useLocation();
  const [selectedMenuItem, setSelectedMenuItem] =
    React.useState("role_management");
  const handleMenuItemClick = (menuId) => {
    setSelectedMenuItem(menuId);
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const tabFromUrl = queryParams.get("tab");

    if (tabFromUrl === "project_management") {
      setSelectedMenuItem("project_management");
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    dispatch(
      HandleApiActions({
        ...userManagementApis.get_country_codes,
        show_toast: false,
      }),
    );

    dispatch(
      HandleApiActions({
        ...settingsApis.get_app_meta_data,
        show_toast: false,
      }),
    );

    dispatch(
      HandleApiActions({
        ...userManagementApis.get_features_list,
        show_toast: false,
      }),
    );

    dispatch(
      HandleApiActions({
        ...userManagementApis.get_roles_list,
        params: {
          page_number: 1,
          page_size: 100,
          sort_column: "created_at",
          sort_order: "ASC",
        },
        show_toast: false,
      }),
    );

    dispatch(
      HandleApiActions({
        ...profileActions.get_profile_details,
        show_toast: false,
        params: {},
      }),
    );
  }, [dispatch]);

  const menuItems = [
    {
      id: "role_management",
      label: "Role Management",
      component: "ArchitectureTypes",
    },
    {
      id: "user_management",
      label: "User Management",
      component: "ManufacturerDetails",
    },
    {
      id: "project_management",
      label: "Project Management",
      component: "PowerTrainClass",
    },
    {
      id: "policies_management",
      label: "Policies",
      component: "PowerTrainClass",
    },
    {
      id: "roletype_management",
      label: "Role Type",
      component: "PowerTrainClass",
    },
  ];

  const renderContent = () => {
    switch (selectedMenuItem) {
      case "role_management":
        return <RolesAndAccessMain />;
      case "user_management":
        return <Users />;
      case "project_management":
        return <ProjectAccess />;
      case "policies_management":
        return <Policies />;
      case "roletype_management":
        return <RoleType />;

      default:
        return <RolesAndAccessMain />;
    }
  };

  return (
    <Box
      sx={{
        ...styled.container,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
      data-testid="AdminSettings"
    >
      <div style={{ display: "flex", height: "100%", margin: 0, padding: 0 }}>
        <div style={{ width: "250px", flexShrink: 0, margin: 0, padding: 0 }}>
          <Paper sx={styled.sidebar}>
            <List component="nav" sx={{ padding: 0 }}>
              {menuItems.map((item) => (
                <React.Fragment key={item.id}>
                  <ListItemButton
                    onClick={() => handleMenuItemClick(item.id)}
                    sx={{
                      ...styled.menuItem,
                      backgroundColor:
                        selectedMenuItem === item.id
                          ? "#FFFFFF"
                          : "transparent",
                      color:
                        selectedMenuItem === item.id ? "#000000" : "#FFFFFF",
                      "&:hover": {
                        backgroundColor: "#8888887c",
                        color: "#FFFFFF",
                        "& .MuiTypography-root": {
                          color: "#FFFFFF",
                        },
                      },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={styled.menuItemContent}>
                          <Typography
                            sx={{
                              ...styled.menuText,
                              color:
                                selectedMenuItem === item.id
                                  ? "#000000"
                                  : "#FFFFFF",
                            }}
                          >
                            {item.label}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItemButton>
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </div>

        <div style={{ flex: 1, margin: 0, padding: 0, height: "100%" }}>
          <Box sx={styled.contentArea}>{renderContent()}</Box>
        </div>
      </div>
    </Box>
  );
}

export default UserManagement;

const styled = {
  container: {
    height: "100%",
    overflow: "hidden",
  },
  sidebar: {
    height: "100%",
    width: "250px",
    backgroundColor: "#000000",
    color: "#FFFFFF",
    overflow: "auto",
    borderRadius: "0 !important",
    border: "none",
    boxShadow: "none",
    margin: 0,
    padding: 0,
  },
  menuItem: {
    color: "#FFFFFF",
    width: "250px",
    height: "42px",
    "&:hover": {
      backgroundColor: "#8888887c",
    },
  },
  menuItemContent: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  menuText: {
    fontStyle: "Open Sans",
    fontSize: "14px",
    fontWeight: 400,
    lineHeight: "20px",
    letterSpacing: "0%",
  },
  contentArea: {
    height: "100%",
    flex: 1,
    overflow: "auto",

    padding: "0",
    scrollbarGutter: "stable",
  },
  defaultContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
  },
};
